/*
 * Copyright © 2020-2025 Ocean Galaxy Inc. All Rights Reserved.
 * @Description: 会员权益限制检查 Hook 和工具函数
 * @LastEditors: yingongjing
 * @LastEditTime: 2025-12-30
 */
import { useMemo } from 'react'
import { useVip } from '../../../../app/hooks'

/**
 * 会员等级限制配置
 */
export const MEMBERSHIP_LIMITS = {
  BASIC: 1, // 基础会员：1张/次
  ADVANCED: 20, // 高级会员：20张/次
  SUPER: 100 // 超级会员：100张/次
}

/**
 * 根据会员状态获取当前限制（普通函数，可在任何地方调用）
 */
export const getMembershipLimit = (
  isVip: boolean,
  isSVip: boolean,
  isVVip: boolean
): number => {
  if (!isVip) {
    return MEMBERSHIP_LIMITS.BASIC
  } else if (isSVip) {
    return MEMBERSHIP_LIMITS.SUPER
  } else if (isVVip) {
    return MEMBERSHIP_LIMITS.ADVANCED
  } else {
    return MEMBERSHIP_LIMITS.BASIC
  }
}

/**
 * 检查是否需要显示会员限制弹窗（普通函数）
 */
export const shouldShowMembershipLimitModal = (
  photoCount: number,
  isVip: boolean,
  isSVip: boolean,
  isVVip: boolean
): boolean => {
  // 超过100张，无论会员等级都需要显示
  if (photoCount > 100) {
    return true
  }
  // 超过当前会员等级限制
  const currentLimit = getMembershipLimit(isVip, isSVip, isVVip)
  if (photoCount > currentLimit) {
    return true
  }
  return false
}

/**
 * 检查是否需要显示会员限制弹窗
 * @param photoCount 选择的图片数量
 * @returns 是否需要显示弹窗
 */
export const useMembershipTierLimit = (photoCount: number) => {
  const { isVip, isSVip, isVVip } = useVip()

  // 获取当前会员等级的限制
  const currentLimit = useMemo(() => {
    return getMembershipLimit(isVip, isSVip, isVVip)
  }, [isVip, isSVip, isVVip])

  // 检查是否超过限制
  const shouldShowLimitModal = useMemo(() => {
    return shouldShowMembershipLimitModal(photoCount, isVip, isSVip, isVVip)
  }, [photoCount, isVip, isSVip, isVVip])

  // 获取会员等级信息
  const membershipInfo = useMemo(() => {
    if (!isVip) {
      return {
        tier: '基础会员',
        limit: MEMBERSHIP_LIMITS.BASIC,
        isBasic: true,
        isAdvanced: false,
        isSuper: false
      }
    } else if (isSVip) {
      return {
        tier: '超级会员',
        limit: MEMBERSHIP_LIMITS.SUPER,
        isBasic: false,
        isAdvanced: false,
        isSuper: true
      }
    } else if (isVVip) {
      return {
        tier: '高级会员',
        limit: MEMBERSHIP_LIMITS.ADVANCED,
        isBasic: false,
        isAdvanced: true,
        isSuper: false
      }
    } else {
      return {
        tier: '基础会员',
        limit: MEMBERSHIP_LIMITS.BASIC,
        isBasic: true,
        isAdvanced: false,
        isSuper: false
      }
    }
  }, [isVip, isSVip, isVVip])

  return {
    shouldShowLimitModal,
    currentLimit,
    membershipInfo,
    isOver100: photoCount > 100
  }
}
